import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Check, Loader2, Share2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { clipApi } from '../api/clipApi'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/axios'
import type { NewsItem } from '../types/news.types'

interface ShareClipButtonProps {
  keyword: string
  items: NewsItem[]
  summary?: string
}

/**
 * Shares the whole keyword tab as a clipping on our own domain.
 *
 * Distinct from `ShareButton`, which shares a single article and correctly points at
 * the article itself. This is the growth loop: a PR person forwarding a clipping to
 * their team is doing their job, and the recipients land here instead of on Naver.
 */
export function ShareClipButton({ keyword, items, summary }: ShareClipButtonProps) {
  const [copied, setCopied] = useState(false)

  const { mutate: share, isPending } = useMutation({
    mutationFn: () => clipApi.createClip({ keyword, items, summary }),
    onSuccess: async ({ path }) => {
      const url = `${window.location.origin}${path}`
      try {
        // Native share sheet on mobile, clipboard everywhere else.
        if (navigator.share) {
          await navigator.share({ title: `${keyword} 뉴스 클리핑`, url })
          return
        }
        await navigator.clipboard.writeText(url)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
        toast.success('클리핑 링크를 복사했어요', url)
      } catch {
        // AbortError when the user dismisses the share sheet, or a clipboard denial.
        // The clipping exists either way, so show the link rather than an error.
        toast.success('클리핑을 만들었어요', url)
      }
    },
    onError: (error) => {
      toast.error('클리핑을 만들지 못했어요', getErrorMessage(error))
    },
  })

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => share()}
      disabled={isPending || items.length === 0}
    >
      {isPending ? (
        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
      ) : copied ? (
        <Check className="mr-1.5 h-4 w-4" />
      ) : (
        <Share2 className="mr-1.5 h-4 w-4" />
      )}
      {copied ? '복사됨' : '클리핑 공유'}
    </Button>
  )
}
